/**
 * Interface for StillImageBackground background images config
 */
import {PunkRocker} from "../PunkRocker";
import {Helper} from "../../core/utils/Helper";
import {IDimOrientation, LayoutDimData, ResizeHelper} from "../../core/utils/ResizeHelper";
import {PREngineManager} from "../punkEngine/PREngineManager";
import {SpinePlayer} from "../../core/displayobject/SpinePlayer";
import {gsap} from "gsap";
import {GameConfig} from "../config/base/GameConfig";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {PRGameMode} from "../server/PRGameMode";

/*export enum BGType {
    MAIN = "mainBackground"
}*/

export class Background extends PIXI.Container {

    protected _graphOverlay: PIXI.Graphics;
    protected _timeline: gsap.core.Timeline;
    //protected _fogSpine: PIXI.spine.Spine;
    protected _bgSpine: SpinePlayer;
    protected _frameSpine: SpinePlayer;
    private bottomTrainVisible: boolean = false;

    constructor() {
        super();
        this.name = "Background";

        this.init();
    }

    public changeGraphicAlpha(duration: number, _alpha: number): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline);
        if (this._graphOverlay.alpha == _alpha) {
            return this._timeline;
        }
        const oldAlpha: number = this._graphOverlay.alpha;
        this._timeline.fromTo(this._graphOverlay, {
            alpha: oldAlpha
        }, {
            alpha: _alpha,
            duration: duration
        });
        return this._timeline;
    }

    public playAttentionBlackFlash(durr: number, _alphaMax: number, _alpha: number): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline);
        const oldAlpha = this._graphOverlay.alpha;
        this._timeline.fromTo(this._graphOverlay, {
            alpha: oldAlpha
        }, {
            alpha: _alphaMax,
            duration: durr * 0.2
        });
        this._timeline.fromTo(this._graphOverlay, {
            alpha: _alphaMax
        }, {
            alpha: _alpha,
            ease: "power2.out",
            duration: durr * 0.8
        });
        return this._timeline;
    }

    protected init(): void {
        this._graphOverlay = new PIXI.Graphics();
        this.addChild(this._graphOverlay);
        this._graphOverlay.alpha = 0;

        this._bgSpine = this.addChild(new SpinePlayer("gameBG"));
        this._bgSpine.visible = false;
        this._bgSpine.position.set(360, 336);
        this._bgSpine.name = "bgSpine";

        // this._fogSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("fog"));
        // this._fogSpine.state.setAnimation(0, 'Idle_Fog', true);
        // this._fogSpine.alpha = 0.4;
        // this._fogSpine.scale.set(2, 2);
        // StageManager.getLayer(PunkGameConfig.LAYERS.FOG.name).addChild(this._fogSpine);
        // this.addChild(this._fogSpine);

        this._frameSpine = this.addChild(new SpinePlayer("reelFrame"));
        this._frameSpine.visible = false;
        this._frameSpine.position.set(360, 360);
        this._frameSpine.name = "frameSpine";

        this.onResize();
    }

    protected RePaint(posX: number, posY: number, width: number, height: number): void {
        this._graphOverlay.clear();
        this._graphOverlay.beginFill(0x000000, 1);
        this._graphOverlay.drawRect(posX, posY, width, height);
        this._graphOverlay.endFill();
    }

    // #354
    public fromBonusToMain(): void {
        this.setBgAnim((PunkRocker.model.gameData.mode === PRGameMode.FREESPIN_LEVEL_THREE ? "bonusGame2ToMainGame" : "bonusGameToMainGame")).eventCallback("onComplete", () => {
            this.setBgAnim("mainGameStatic", true);
        });

        this.setFrameAnim("bonusGameToMainGame").eventCallback("onComplete", () => {
            this.setFrameAnim("mainGameStatic");
        });

        // #365
        PunkRocker.foreground.playForegroundAnimation("static");

        PunkRocker.BURNING_JUMPING_WILDS_COUNT = 0;
    }

    public setBgAnim(animType: string, loop: boolean = false): gsap.core.Timeline {
        this._bgSpine.visible = true;

        const duration: number = SpinePlayer.fetchDuration("gameBG", animType) || 0.01;
        return this._bgSpine.play({
            animation: animType,
            loop: loop,
            showOnEnd: true,
            duration: duration
        });
    }

    public setFrameAnim(animType: string, isRestoreState: boolean = false, loop: boolean = false): gsap.core.Timeline {
        this._frameSpine.visible = true;

        if(animType === "bonusGameToMainGame") {
            PunkRocker.trainsManager.toggleBottomTrain(false);
        }

        const duration: number = SpinePlayer.fetchDuration("reelFrame", animType) || 0.01;
        return this._frameSpine.play({
            animation: animType,
            loop: loop,
            showOnEnd: true,
            duration: duration,
            onStart: ()=>{
                if(animType === "mainGameToBonusGame" || animType === "bonusGameStatic" || animType === "bonusGameStatic2") {
                    PunkRocker.trainsManager.toggleBottomTrain(true, isRestoreState);
                }
            }
        });
    }

    public onBottomTrainShow(flag: boolean, isRestore: boolean = false): void {
        this.bottomTrainVisible = flag;
        const scaledReelDim: LayoutDimData = flag ? PREngineManager.dimScaled : PREngineManager.dim;

        if (flag) {
            gsap.to(this._frameSpine.position, {
                x: 360,
                y: NolimitApplication.isLandscape ? 330 : 360,
                duration: isRestore ? 0.001 : GameConfig.FRAME_EXPAND_DUR
            });
            gsap.to(this._frameSpine.scale, {
                x: scaledReelDim.scale.x,
                y: scaledReelDim.scale.y,
                duration: isRestore ? 0.001 : GameConfig.FRAME_EXPAND_DUR
            });
        } else {
            this._frameSpine.position.set(360, 360);
            this._frameSpine.scale.set(scaledReelDim.scale.x, scaledReelDim.scale.y);
        }
    }

    onResize(): void {
        this.RePaint(-400, -480, 1680, 1680);

        const scaledReelDim: LayoutDimData = this.bottomTrainVisible ? PREngineManager.dimScaled: PREngineManager.dim;

        if(NolimitApplication.isLandscape && this.bottomTrainVisible) {
            this._frameSpine.position.set(360, 330);
        }else{
            this._frameSpine.position.set(360, 360);
        }
        this._frameSpine.scale.set(scaledReelDim.scale.x, scaledReelDim.scale.y);
    }
}